import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useConsumo } from "../contexts/ConsumoContext";
import { sufijoIva, modoConRespaldo, useModoIva } from "../lib/planes";

// ═══════════════════════════════════════════════════════════════════════════════
// LIM.5 (4 Oct 2026) -- consumo de Sofi, aviso de pausa y recarga.
//
// 🔴 TEXTOS APROBADOS POR JESÚS EL 4 OCT 2026, LITERALES. Cambiar una coma es texto de cara al
//    cliente: parada de copy. Viven SÓLO aquí; los números los pone el servidor.
// 🔴 Sin texto para errores ni para «cargando»: no hay textos aprobados, así que sin dato no se
//    pinta nada y un fallo al iniciar la recarga sólo devuelve el botón a su estado.
// ═══════════════════════════════════════════════════════════════════════════════

const num = (n) => Number(n || 0).toLocaleString("es-MX");
const precio = (n) => Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 });

export const TEXTOS = {
  titulo: "Conversaciones de este mes", // B1
  incluidas: (usadas, limite) => `${num(usadas)} de ${num(limite)} incluidas en tu plan`, // B2
  adicionales: (saldo) => `Conversaciones adicionales disponibles: ${num(saldo)}`, // B3
  aviso80: "Ya usaste el 80 % de las conversaciones de tu plan este mes.", // B4
  usandoPaquete:
    "Se acabaron las conversaciones de tu plan este mes. Tu asistente sigue respondiendo con tus conversaciones adicionales.", // B5
  pausa:
    'Tu asistente está en pausa: se acabaron las conversaciones de este mes. A quien te escriba le llegará una sola vez el mensaje "Hola, recibimos tu mensaje." Sofi sigue detectando urgencias.', // B6
  // PRECIOS.1 I2 (autorizado por el director, 11 Oct 2026): con `iva.modo = mas_iva` el precio lleva
  // « + IVA» detrás; sin el campo o con `incluido`, el texto B7 de siempre, carácter a carácter.
  recargar: (conv, total, modo) => `Recargar ${num(conv)} conversaciones — $${precio(total)}${sufijoIva(modo)}`, // B7
  cambiarPlan: "Cambiar de plan", // B7
  esperar: (fecha) => `O espera al ${fecha}, cuando se reinician tus conversaciones.`, // B7
  speiPendiente: "Tu pago está pendiente. Las conversaciones se suman cuando se confirme.", // B8; texto neutral de P-43, aprobado por Jesús el 3 Nov 2026.
  recargaOk: (conv) => `Listo: se sumaron ${num(conv)} conversaciones.`, // B9
};

// LIM.11 I8b (5 Oct 2026) -- la prueba gratis en el panel.
// 🔴 TEXTOS APROBADOS POR JESÚS, LITERALES: `IA/contexto-surit.md` («Q10 textos de panel») y la
//    sección E6 de `IA/reportes/LIM/lim7-borradores.md` (E6a/E6b/E6c, variante de clínicas).
//    Cambiar una coma es texto de cara al cliente: parada de copy. Los «100» son parte del texto
//    aprobado: la tarjeta de prueba activa sólo se pinta con `tope` == 100 (como E6 en agency).
export const TEXTOS_PRUEBA = {
  titulo: "Tu prueba gratis", // P-1
  tituloPorTerminar: "Tu prueba gratis está por terminar", // E6b
  terminaEl: (negocio, fecha) =>
    `Tu prueba gratis en ${negocio} termina el ${fecha} o al llegar a 100 conversaciones, lo que pase primero.`, // E6b
  inicio: (fecha) => `Inicio: ${fecha}`, // E6a
  usadas: (n) => `Conversaciones usadas: ${num(n)} de 100`, // E6b
  terminaMasTardar: (fecha) => `Termina a más tardar: ${fecha}`, // E6a/E6b
  sePausara: "Cuando termine, tu asistente se pausará hasta que confirmes.", // E6b
  sigaSinPausa: "Para que siga respondiendo sin pausa, elige tu plan y paga.", // P-2
  terminada: "Tu prueba gratis terminó y Sofi está en pausa hasta que confirmes.", // P-4
  mientras: "Mientras tanto, a quien te escriba le llegará una sola vez el mensaje «Hola, recibimos tu mensaje.»", // E6c
  urgencias: "Sofi sigue detectando urgencias.", // E6c (clínicas)
  vuelvaAResponder: "Para que tu asistente vuelva a responder, elige tu plan y paga.", // P-2
  pendiente: "Tu prueba gratis empieza cuando tu asistente quede conectado a WhatsApp.", // P-3
  confirmar: "Confirmar mi plan",
};

// Los estados en los que hay un límite que contar. En `sin_limite`, `sin_sofi` y
// `no_disponible` no se pinta nada: no hay cifra cierta que enseñar.
export const ESTADOS_CON_CONSUMO = new Set(["normal", "aviso_80", "usando_paquete", "limite_alcanzado", "pausa"]);
// Donde «Cambiar de plan» y «O espera al…» tienen sentido: ya no quedan conversaciones del plan.
const ESTADOS_SIN_CONVERSACIONES = new Set(["pausa", "limite_alcanzado"]);

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto",
  "septiembre", "octubre", "noviembre", "diciembre"];

// `corte` llega como medianoche UTC del primer día del mes siguiente (agency cuenta el mes en
// UTC, `corte_de_uso`). Se leen las partes UTC: en hora de México esa medianoche cae la tarde
// del día anterior, y formatearla en local diría un día equivocado.
export function fechaCorte(iso) {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} de ${MESES[d.getUTCMonth()]}`;
}

export function consumoVisible(consumo) {
  return !!consumo && ESTADOS_CON_CONSUMO.has(consumo.estado) && Number(consumo.limite) > 0;
}

// LIM.11 I8b: qué tarjeta de prueba toca, sólo con lo que dice agency (`estado` y `prueba`).
//   «terminada»  <- estado `prueba_terminada` (también una `activa` ya vencida: agency lo decide)
//   «activa»     <- estado `prueba`, con `tope` == 100 (el número del texto aprobado)
//   «pendiente»  <- `prueba.estado` = `pendiente` (agency deja `estado` como está)
//   null         <- todo lo demás (sin campo, `confirmada`, datos incompletos): lo de LIM.5 igual.
export function modoPrueba(consumo) {
  const p = consumo && consumo.prueba;
  if (!p || typeof p !== "object") return null;
  if (consumo.estado === "prueba_terminada") return "terminada";
  if (consumo.estado === "prueba") return Number(p.tope) === 100 ? "activa" : null;
  if (p.estado === "pendiente") return "pendiente";
  return null;
}

// ¿Hay algo que pintar en la sección de Sofi del Dashboard? (consumo de LIM.5 o la prueba).
export function tarjetaVisible(consumo) {
  return !!modoPrueba(consumo) || consumoVisible(consumo);
}

// Las fechas de la prueba llegan en ISO con la hora de México (`2026-10-12T09:30:00-06:00`):
// se leen la fecha del propio texto, sin convertir zona, con el formato de los correos E6
// («12 de octubre»).
export function fechaPrueba(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof iso === "string" ? iso : "");
  if (!m) return null;
  const mes = Number(m[2]);
  const dia = Number(m[3]);
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  return `${dia} de ${MESES[mes - 1]}`;
}

// Las acciones (B7). Sólo con `configuracion`: el servidor lo exige igual (403), esto sólo evita
// ofrecer una puerta cerrada (DT.71).
function Acciones({ consumo, testid }) {
  const { puede } = useAuth();
  const { iniciarRecarga } = useConsumo();
  const [enviando, setEnviando] = useState(false);
  // PRECIOS.1 I2: `/api/consumo` no trae `iva`; el modo sale del catálogo público (sin él: incluido).
  const modoCatalogo = useModoIva();
  if (!puede("configuracion")) return null;
  const paquete = consumo.paquete;
  // Corrección 1 (LIM.5): con un pago por transferencia pendiente no se ofrece otra recarga,
  // para no comprar dos veces mientras llega el SPEI.
  const conRecarga = !!paquete?.disponible && (consumo.pagos_pendientes || []).length === 0;
  const sinConversaciones = ESTADOS_SIN_CONVERSACIONES.has(consumo.estado);
  const fecha = sinConversaciones ? fechaCorte(consumo.corte) : null;
  if (!conRecarga && !sinConversaciones) return null;

  const recargar = async () => {
    if (enviando) return;
    setEnviando(true);
    const ok = await iniciarRecarga();
    if (!ok) setEnviando(false); // sin texto nuevo: el botón vuelve a estar disponible
  };

  return (
    <div className="mt-3" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {conRecarga && (
          <button
            type="button"
            onClick={recargar}
            disabled={enviando}
            data-testid="consumo-recargar"
            className="bg-violet-600 text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-60"
          >
            {TEXTOS.recargar(paquete.conversaciones, paquete.precio_total, modoConRespaldo(paquete, modoCatalogo))}
          </button>
        )}
        {sinConversaciones && (
          <Link
            to="/app/onboarding"
            data-testid="consumo-cambiar-plan"
            className="bg-white border border-slate-300 text-slate-700 text-xs font-medium px-3 py-2 rounded-lg hover:bg-slate-50"
          >
            {TEXTOS.cambiarPlan}
          </Link>
        )}
      </div>
      {fecha && <p className="text-xs text-slate-600 mt-2" data-testid="consumo-esperar">{TEXTOS.esperar(fecha)}</p>}
    </div>
  );
}

// «Confirmar mi plan» (LIM.11 I8b): el mismo enlace y la misma regla que «Cambiar de plan»
// (sólo con `configuracion`).
function ConfirmarPlan({ testid }) {
  const { puede } = useAuth();
  if (!puede("configuracion")) return null;
  return (
    <div className="mt-3">
      <Link
        to="/app/onboarding"
        data-testid={testid}
        className="inline-block bg-violet-600 text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-violet-700"
      >
        {TEXTOS_PRUEBA.confirmar}
      </Link>
    </div>
  );
}

// Lo que dice la prueba terminada (P-4 y E6c de clínicas): en la tarjeta y en el aviso del layout.
function TextoPruebaTerminada() {
  return (
    <>
      <p data-testid="prueba-terminada">{TEXTOS_PRUEBA.terminada}</p>
      <p className="mt-1" data-testid="prueba-mientras">{TEXTOS_PRUEBA.mientras}</p>
      <p className="mt-1" data-testid="prueba-urgencias">{TEXTOS_PRUEBA.urgencias}</p>
      <p className="mt-1" data-testid="prueba-vuelva">{TEXTOS_PRUEBA.vuelvaAResponder}</p>
    </>
  );
}

function TarjetaPrueba({ consumo, modo }) {
  const { activeClinic } = useAuth();
  const p = consumo.prueba;
  const titulo = modo === "activa" && p.por_terminar === true ? TEXTOS_PRUEBA.tituloPorTerminar : TEXTOS_PRUEBA.titulo;
  const negocio = activeClinic?.name;
  const inicio = fechaPrueba(p.inicio);
  const fin = fechaPrueba(p.fin);
  return (
    <div className="mb-4 rounded-xl bg-white/70 border border-[#DDD6FE] p-4" data-testid="prueba-sofi" data-modo={modo}>
      <div className="text-sm font-semibold text-slate-900" data-testid="prueba-titulo">{titulo}</div>
      {modo === "pendiente" && (
        <p className="mt-2 text-xs text-slate-600" data-testid="prueba-pendiente">{TEXTOS_PRUEBA.pendiente}</p>
      )}
      {modo === "activa" && (<>
        {negocio && fin && (
          <p className="mt-2 text-xs text-slate-600" data-testid="prueba-termina-el">{TEXTOS_PRUEBA.terminaEl(negocio, fin)}</p>
        )}
        <div className="mt-2 text-xs text-slate-600 space-y-0.5" data-testid="prueba-filas">
          {inicio && <div data-testid="prueba-inicio">{TEXTOS_PRUEBA.inicio(inicio)}</div>}
          <div data-testid="prueba-usadas">{TEXTOS_PRUEBA.usadas(p.conversaciones)}</div>
          {fin && <div data-testid="prueba-fin">{TEXTOS_PRUEBA.terminaMasTardar(fin)}</div>}
        </div>
        <p className={`mt-3 text-xs rounded-lg px-3 py-2 ${p.por_terminar === true
          ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-violet-50 border border-violet-200 text-violet-900"}`}
          data-testid="prueba-aviso">
          {TEXTOS_PRUEBA.sePausara} {TEXTOS_PRUEBA.sigaSinPausa}
        </p>
        <ConfirmarPlan testid="prueba-confirmar" />
      </>)}
      {modo === "terminada" && (<>
        <div className="mt-3 text-xs rounded-lg bg-rose-50 border border-rose-200 text-rose-900 px-3 py-2">
          <TextoPruebaTerminada />
        </div>
        <ConfirmarPlan testid="prueba-confirmar" />
      </>)}
    </div>
  );
}

// La tarjeta de consumo, DENTRO de la sección «Sofi este mes» del Dashboard (no tiene título de
// sección propio: B1 es el subtítulo del bloque).
export function TarjetaConsumo() {
  const { consumo, recarga, recargaConv } = useConsumo();
  const modo = modoPrueba(consumo);
  if (modo) return <TarjetaPrueba consumo={consumo} modo={modo} />;
  if (!consumoVisible(consumo)) return null;
  const { estado, usadas, limite } = consumo;
  const pct = Math.max(0, Math.min(100, Number(consumo.porcentaje) || 0));
  // B2 cuenta las INCLUIDAS en el plan: pasado el límite son todas, no más.
  const incluidasUsadas = Math.min(Number(usadas) || 0, Number(limite));
  const colorBarra = estado === "aviso_80" ? "bg-amber-500"
    : (estado === "pausa" || estado === "limite_alcanzado") ? "bg-rose-500" : "bg-violet-600";

  return (
    <div className="mb-4 rounded-xl bg-white/70 border border-[#DDD6FE] p-4" data-testid="consumo-sofi">
      <div className="text-sm font-semibold text-slate-900">{TEXTOS.titulo}</div>
      <div className="mt-2 h-2 rounded-full bg-violet-100 overflow-hidden" aria-hidden="true">
        <div className={`h-full ${colorBarra}`} style={{ width: `${pct}%` }} data-testid="consumo-barra" />
      </div>
      <div className="text-xs text-slate-600 mt-1.5" data-testid="consumo-incluidas">{TEXTOS.incluidas(incluidasUsadas, limite)}</div>
      {consumo.saldo_paquetes > 0 && (
        <div className="text-xs text-slate-600 mt-0.5" data-testid="consumo-adicionales">{TEXTOS.adicionales(consumo.saldo_paquetes)}</div>
      )}
      {recarga === "acreditada" && recargaConv > 0 && (
        <p className="mt-3 text-xs rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2" data-testid="consumo-recarga-ok">
          {TEXTOS.recargaOk(recargaConv)}
        </p>
      )}
      {estado === "aviso_80" && (
        <p className="mt-3 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2" data-testid="consumo-aviso80">{TEXTOS.aviso80}</p>
      )}
      {estado === "usando_paquete" && (
        <p className="mt-3 text-xs rounded-lg bg-violet-50 border border-violet-200 text-violet-900 px-3 py-2" data-testid="consumo-usando-paquete">{TEXTOS.usandoPaquete}</p>
      )}
      {estado === "pausa" && (
        <p className="mt-3 text-xs rounded-lg bg-rose-50 border border-rose-200 text-rose-900 px-3 py-2" data-testid="consumo-pausa">{TEXTOS.pausa}</p>
      )}
      {(consumo.pagos_pendientes || []).length > 0 && (
        <p className="mt-3 text-xs rounded-lg bg-slate-50 border border-slate-200 text-slate-700 px-3 py-2" data-testid="consumo-spei">{TEXTOS.speiPendiente}</p>
      )}
      <Acciones consumo={consumo} testid="consumo-acciones" />
    </div>
  );
}

// El aviso de pausa (B6) bajo la barra superior, en TODAS las pantallas y para toda sesión.
// En el Dashboard no se repite: allí lo lleva la tarjeta de consumo.
export function AvisoPausa({ ocultar }) {
  const { consumo } = useConsumo();
  if (ocultar) return null;
  // LIM.11 I8b: la prueba terminada también deja a Sofi en pausa; mismo sitio, sus textos.
  if (modoPrueba(consumo) === "terminada") {
    return (
      <div className="px-4 sm:px-6 py-3 bg-rose-50 border-b border-rose-200" data-testid="aviso-prueba-terminada">
        <div className="text-sm text-rose-900"><TextoPruebaTerminada /></div>
        <ConfirmarPlan testid="aviso-prueba-confirmar" />
      </div>
    );
  }
  if (!consumoVisible(consumo) || consumo.estado !== "pausa") return null;
  return (
    <div className="px-4 sm:px-6 py-3 bg-rose-50 border-b border-rose-200" data-testid="aviso-pausa">
      <p className="text-sm text-rose-900">{TEXTOS.pausa}</p>
      <Acciones consumo={consumo} testid="aviso-pausa-acciones" />
    </div>
  );
}
